import { ScheduleActivityTitle } from "@/features/calendar/components/ScheduleActivityTitle";
import { type CoWorkerScheduleActivity } from "@/features/calendar/types";
import {
  Button,
  ListView,
  ListViewItem,
  Text,
  Variables,
  calendarIcon,
  clockIcon,
  drillIcon,
  pencilIcon,
  personIcon,
  trashCanIcon,
} from "@repo/skapa";
import { t } from "@repo/translation";
import {
  ButtonsContainer,
  CoWorkerName,
  FlexDiv,
  LocalizedDateAndTime,
  LocalizedDateTimeRange,
  RegionContainer,
  SectionContainer,
  translateScheduleShiftType,
  useSettings,
} from "@repo/ui";
import { trackAnalytics } from "@repo/utils";

import { useModals } from "../hooks";

interface ScheduleActivityViewProps {
  item: CoWorkerScheduleActivity;
  onEdit: () => void;
  onDelete: () => void;
  loading?: "EDITING" | "DELETING";
}

export const ScheduleActivityView = ({
  item,
  onEdit,
  onDelete,
  loading,
}: ScheduleActivityViewProps) => {
  const { pushModal } = useModals();
  const { calendar } = useSettings();

  return (
    <RegionContainer>
      <SectionContainer>
        <FlexDiv justifyContent="space-between" gap={Variables.space50}>
          <Text textStyle="Heading.M">
            <ScheduleActivityTitle item={item} />
          </Text>

          <ButtonsContainer>
            {calendar?.edit_schedule_entries && (
              <Button
                size="small"
                iconOnly
                ssrIcon={pencilIcon}
                title={t("action.edit")}
                onClick={() => {
                  trackAnalytics("edit_task_time");
                  onEdit();
                }}
                loading={loading === "EDITING"}
              />
            )}
            {calendar?.delete_schedule_entries && (
              <Button
                size="small"
                iconOnly
                ssrIcon={trashCanIcon}
                title={t("action.delete")}
                onClick={() => {
                  pushModal({
                    type: "CONFIRM",
                    onConfirm: onDelete,
                    title: t("action.delete_schedule"),
                    message: t("prompt.confirm_delete_schedule"),
                  });
                  trackAnalytics("confirm_delete_schedule");
                }}
                loading={loading === "DELETING"}
              />
            )}
          </ButtonsContainer>
        </FlexDiv>

        <ListView showDivider={false} id={`activity-details-${item.id}`}>
          <ListViewItem
            leadingIcon={clockIcon}
            title={t("label.date_and_time")}
            description={
              <LocalizedDateTimeRange fromDate={item.start} toDate={item.end} />
            }
          />

          <ListViewItem
            leadingIcon={personIcon}
            title={t("label.assigned_coworker")}
            description={<CoWorkerName email={item.emailId} />}
          />

          <ListViewItem
            leadingIcon={drillIcon}
            title={t("label.activity")}
            description={item.activity}
          />

          <ListViewItem
            leadingIcon={calendarIcon}
            title={t("label.shift_type")}
            description={translateScheduleShiftType(item.shiftType)}
          />

          {item.editedAt && (
            <ListViewItem
              leadingIcon={pencilIcon}
              title={t("label.edited_at")}
              description={
                <LocalizedDateAndTime date={new Date(item.editedAt)} />
              }
            />
          )}
          {item.editedBy && (
            <ListViewItem
              leadingIcon={pencilIcon}
              title={t("label.edited_by")}
              description={<CoWorkerName email={item.editedBy} />}
            />
          )}
        </ListView>
      </SectionContainer>
    </RegionContainer>
  );
};
